/**
 * Build a URL with a text fragment anchor (#:~:text=...) for deep-linking
 * into long documents. Supported by Chrome, Edge, and Safari.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/URI/Fragment/Text_fragments
 */

/**
 * Append a text fragment to a base URL so the browser scrolls to and
 * highlights the matching text on the target page.
 *
 * Returns the original URL unchanged when no usable anchor text is provided.
 */
export function buildTextFragmentUrl(
  baseUrl: string | undefined,
  anchorText: string | undefined,
): string | undefined {
  if (!baseUrl) return baseUrl

  const cleaned = cleanAnchorText(anchorText)
  if (!cleaned) return baseUrl

  const encoded = encodeURIComponent(cleaned)
  const separator = baseUrl.includes('#') ? '' : '#'
  return `${baseUrl}${separator}:~:text=${encoded}`
}

/**
 * Extract a short, fragment-safe substring from raw anchor text.
 * Returns empty string if the input is too short to be useful.
 */
function cleanAnchorText(raw: string | undefined): string {
  if (!raw) return ''

  const trimmed = raw
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (trimmed.length < 15) return ''

  // Use first ~60 chars, breaking at a word boundary
  if (trimmed.length <= 60) return trimmed

  const cut = trimmed.slice(0, 60)
  const lastSpace = cut.lastIndexOf(' ')
  return lastSpace > 30 ? cut.slice(0, lastSpace) : cut
}
